// src/pages/Departments.js
import { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useDepartmentStore from '../store/useDepartmentStore';
import useAuthStore from '../store/authStore';

export default function Departments() {
  const [name, setName] = useState('');
  const [editDepartment, setEditDepartment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const { departments, setDepartments, addDepartment, updateDepartment, deleteDepartment } =
    useDepartmentStore();

  const { access, orgId, setOrgId } = useAuthStore();

useEffect(() => {
  if (!access) {
    alert('Access token missing. Please log in again.');
    navigate('/');
    return;
  }

  try {
    const decodedToken = JSON.parse(atob(access.split('.')[1]));
    const decodedOrgId = decodedToken.org_id;

    if (!decodedOrgId) {
      alert('Organization ID missing in token. Please log in again.');
      navigate('/');
    } else {
      setOrgId(decodedOrgId);
      fetchDepartments(decodedOrgId);
    }
  } catch (error) {
    console.error('Token decode error:', error);
    alert('Invalid token. Please log in again.');
    navigate('/');
  }
}, [access, navigate]);


  const fetchDepartments = async (organizationId) => {
    try {
      const res = await axios.get(`/api/departments/?organization=${organizationId}`);
      setDepartments(res.data);
    } catch (err) {
      console.error('Fetch departments error:', err.response ? err.response.data : err);
      if (err.response?.status === 401) {
        alert('Session expired. Please log in again.');
        navigate('/');
      }
    }
  };

  const handleSubmit = async () => {
    if (!orgId || name.trim() === '') {
      toast.error('Department name cannot be empty.');
      return;
    }

    const payload = {
      name: name.trim(),
      organization: orgId,
    };

    try {
      if (editDepartment) {
        const res = await axios.put(`/api/departments/${editDepartment.id}/`, payload);
        updateDepartment(res.data);
        toast.success('Department updated successfully');
      } else {
        const res = await axios.post('/api/departments/', payload);
        addDepartment(res.data);
        toast.success('Department added successfully');
      }
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error('Submit error:', err.response ? err.response.data : err);
      toast.error('Error adding/updating department');
      if (err.response?.status === 401) {
        alert('Unauthorized: Please log in again.');
        navigate('/');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;

    try {
      await axios.delete(`/api/departments/${id}/`);
      deleteDepartment(id);
      toast.warn('Department deleted');
    } catch (err) {
      console.error('Delete error:', err.response ? err.response.data : err);
      toast.error('Error deleting department');
      if (err.response?.status === 401) {
        alert('Unauthorized: Please log in again.');
        navigate('/');
      }
    }
  };

  const handleEdit = (dept) => {
    setName(dept.name);
    setEditDepartment(dept);
    setShowModal(true);
  };

  const resetForm = () => {
    setName('');
    setEditDepartment(null);
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Manage Departments</h2>

      <button
        onClick={() => {
          resetForm();
          setShowModal(true);
        }}
        className="mb-4 bg-green-500 text-white px-4 py-2 rounded"
      >
        Add Department
      </button>

      <table className="min-w-full table-auto border">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-4 py-2 border">#</th>
            <th className="px-4 py-2 border">Department Name</th>
            <th className="px-4 py-2 border">Actions</th>
          </tr>
        </thead>
        <tbody>
          {departments.map((dept, index) => (
            <tr key={dept.id} className="text-center">
              <td className="px-4 py-2 border">{index + 1}</td>
              <td className="px-4 py-2 border">{dept.name}</td>
              <td className="px-4 py-2 border">
                <button
                  onClick={() => handleEdit(dept)}
                  className="text-yellow-500 hover:text-yellow-700 mr-2"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(dept.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">
              {editDepartment ? 'Edit Department' : 'Add Department'}
            </h3>
            <input
              type="text"
              className="border p-2 w-full rounded mb-4"
              placeholder="Department Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(false);
                }}
                className="bg-gray-300 px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                {editDepartment ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
